/**
 * Mobile Sidebar Component - Collapsible groups with smooth animations
 * Supports swipe gestures for opening/closing
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useInstance } from '../../contexts/InstanceContext';
import { getLogo, getBusinessName } from '../../lib/logos';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import { InstanceSelector } from './InstanceSelector';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../ui/collapsible';
import {
  LayoutDashboard,
  ShoppingCart,
  Package,
  FolderTree,
  Users,
  UserCog,
  Shield,
  Table2,
  ClipboardList,
  Wallet,
  CreditCard,
  BarChart3,
  FileText,
  PackageSearch,
  Utensils,
  Crown,
  Boxes,
  ArrowLeftRight,
  Settings,
  Building2,
  Tag,
  Ruler,
  ChevronDown,
  X,
  Printer,
  Briefcase,
  Headphones,
} from 'lucide-react';

// Icon map
const iconMap = {
  LayoutDashboard,
  ShoppingCart,
  Package,
  FolderTree,
  Users,
  UserCog,
  Shield,
  Table2,
  ClipboardList,
  Wallet,
  CreditCard,
  BarChart3,
  FileText,
  PackageSearch,
  Utensils,
  Crown,
  Boxes,
  ArrowLeftRight,
  Settings,
  Building2,
  Tag,
  Ruler,
  Printer,
  Briefcase,
  Headphones,
};

// Navigation structure
const navigationItems = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: 'LayoutDashboard',
    path: '/',
    type: 'link',
  },
  {
    id: 'cadastros',
    label: 'Cadastros',
    icon: 'Package',
    type: 'group',
    children: [
      { label: 'Produtos', path: '/products', icon: 'Package' },
      { label: 'Serviços', path: '/services', icon: 'Briefcase' },
      { label: 'Categorias', path: '/categories', icon: 'FolderTree' },
      { label: 'Combos', path: '/combos', icon: 'Utensils' },
      { label: 'Clientes', path: '/customers', icon: 'Users' },
      { label: 'Fornecedores', path: '/suppliers', icon: 'Building2' },
      { label: 'Marcas', path: '/brands', icon: 'Tag' },
      { label: 'Unidades', path: '/units-of-measure', icon: 'Ruler' },
    ],
  },
  {
    id: 'operacoes',
    label: 'Operações',
    icon: 'ClipboardList',
    type: 'group',
    children: [
      { label: 'Mesas', path: '/tables', icon: 'Table2' },
      { label: 'Comandas', path: '/orders', icon: 'ClipboardList' },
      { label: 'Caixas', path: '/cashiers', icon: 'Wallet' },
    ],
  },
  {
    id: 'estoque',
    label: 'Estoque',
    icon: 'Boxes',
    type: 'group',
    children: [
      { label: 'Lotes', path: '/stock/batches', icon: 'Boxes' },
      { label: 'Movimentações', path: '/stock/movements', icon: 'ArrowLeftRight' },
    ],
  },
  {
    id: 'relatorios',
    label: 'Relatórios',
    icon: 'BarChart3',
    type: 'group',
    children: [
      { label: 'Vendas', path: '/reports/sales', icon: 'BarChart3' },
      { label: 'Caixa', path: '/reports/cashier', icon: 'FileText' },
      { label: 'Estoque', path: '/reports/stock', icon: 'PackageSearch' },
    ],
  },
  {
    id: 'config',
    label: 'Configurações',
    icon: 'Settings',
    type: 'group',
    children: [
      { label: 'Usuários', path: '/users', icon: 'UserCog' },
      { label: 'Perfis', path: '/profiles', icon: 'Shield' },
      { label: 'Pagamentos', path: '/payment-methods', icon: 'CreditCard' },
      { label: 'Impressoras', path: '/settings/printers', icon: 'Printer' },
      { label: 'Sistema', path: '/settings', icon: 'Settings' },
    ],
  },
  {
    id: 'suporte',
    label: 'Suporte',
    icon: 'Headphones',
    path: '/tickets',
    type: 'link',
  },
];

// Mobile Nav Link
function MobileNavLink({ item, onClick, isActive }) {
  const Icon = iconMap[item.icon] || Package;
  
  return (
    <Link
      to={item.path}
      onClick={onClick}
      data-testid={`mobile-nav-${item.id || item.path.replace(/\//g, '-')}`}
      className={cn(
        'flex items-center gap-3 px-4 py-3 rounded-xl transition-all active:scale-[0.98]',
        isActive
          ? 'bg-primary text-primary-foreground shadow-sm'
          : 'hover:bg-muted active:bg-muted'
      )}
    >
      <Icon className="h-5 w-5" />
      <span className="font-medium">{item.label}</span>
    </Link>
  );
}

// Mobile Nav Group
function MobileNavGroup({ item, onItemClick }) {
  const location = useLocation();
  const Icon = iconMap[item.icon] || Package;
  
  // Check if any child is active
  const isGroupActive = item.children?.some(child => 
    location.pathname === child.path || location.pathname.startsWith(child.path + '/')
  );
  
  // Start open if group is active
  const [isOpen, setIsOpen] = useState(isGroupActive);
  
  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger asChild>
        <button
          data-testid={`mobile-nav-group-${item.id}`}
          className={cn(
            'w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all',
            isGroupActive 
              ? 'bg-primary/10 text-primary' 
              : 'hover:bg-muted active:bg-muted'
          )}
        >
          <Icon className="h-5 w-5" />
          <span className="font-medium flex-1 text-left">{item.label}</span>
          <ChevronDown 
            className={cn(
              'h-4 w-4 transition-transform duration-200',
              isOpen && 'rotate-180'
            )} 
          />
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent className="overflow-hidden data-[state=open]:animate-collapsible-down data-[state=closed]:animate-collapsible-up">
        <div className="pl-4 mt-1 space-y-1">
          {item.children?.map((child) => {
            const ChildIcon = iconMap[child.icon] || Package;
            const isActive = location.pathname === child.path;
            
            return (
              <Link
                key={child.path}
                to={child.path}
                onClick={onItemClick}
                data-testid={`mobile-nav-${child.path.replace(/\//g, '-')}`}
                className={cn(
                  'flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all active:scale-[0.98]',
                  isActive
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                )}
              >
                <ChildIcon className="h-4 w-4" />
                <span className="text-sm font-medium">{child.label}</span>
              </Link>
            );
          })}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

// Main Mobile Sidebar Component
export default function MobileSidebar({ isOpen, onClose, onOpen }) {
  const { t } = useTranslation();
  const location = useLocation();
  const { isAdminMaster, currentInstance } = useInstance();
  const sidebarRef = useRef(null);
  const logoSrc = getLogo(currentInstance, 'iconDark');
  const businessName = getBusinessName(currentInstance);
  
  // Swipe gesture state
  const touchStartRef = useRef({ x: 0, y: 0, time: 0 });
  const touchCurrentRef = useRef({ x: 0 });
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  
  // Swipe configuration
  const SWIPE_THRESHOLD = 80;
  const EDGE_WIDTH = 25;
  const VELOCITY_THRESHOLD = 0.5;
  
  // Handle touch start - detect edge swipe for opening
  const handleTouchStart = useCallback((e) => {
    const touch = e.touches[0];
    touchStartRef.current = { 
      x: touch.clientX, 
      y: touch.clientY,
      time: Date.now()
    };
    touchCurrentRef.current = { x: touch.clientX };
    
    // Start dragging only if:
    // 1. Sidebar is open (can close by swiping left)
    // 2. Touch started from left edge (can open by swiping right)
    if (isOpen || touch.clientX <= EDGE_WIDTH) {
      setIsDragging(true);
    }
  }, [isOpen]);
  
  // Handle touch move - update drag offset for visual feedback
  const handleTouchMove = useCallback((e) => {
    if (!isDragging) return;
    
    const touch = e.touches[0];
    const deltaX = touch.clientX - touchStartRef.current.x;
    const deltaY = touch.clientY - touchStartRef.current.y;
    
    // Ignore if vertical swipe is dominant
    if (Math.abs(deltaY) > Math.abs(deltaX) * 1.5) {
      setIsDragging(false);
      setDragOffset(0);
      return;
    }
    
    touchCurrentRef.current = { x: touch.clientX };
    
    if (isOpen) {
      // When open, only allow dragging left (negative offset, capped at 0)
      setDragOffset(Math.min(0, deltaX));
    } else {
      // When closed, only allow dragging right from edge (positive offset, capped at sidebar width)
      setDragOffset(Math.max(0, Math.min(320, deltaX)));
    }
  }, [isDragging, isOpen]);
  
  // Handle touch end - determine if swipe was enough to toggle
  const handleTouchEnd = useCallback(() => {
    if (!isDragging) return;
    
    const deltaX = touchCurrentRef.current.x - touchStartRef.current.x;
    const deltaTime = Date.now() - touchStartRef.current.time;
    const velocity = Math.abs(deltaX) / deltaTime;
    
    // Fast swipe or exceeded threshold
    const shouldToggle = Math.abs(deltaX) > SWIPE_THRESHOLD || velocity > VELOCITY_THRESHOLD;
    
    if (shouldToggle) {
      if (isOpen && deltaX < 0) {
        // Swipe left when open -> close
        onClose();
      } else if (!isOpen && deltaX > 0) {
        // Swipe right when closed -> open
        onOpen?.();
      }
    }
    
    setIsDragging(false);
    setDragOffset(0);
  }, [isDragging, isOpen, onClose, onOpen]);
  
  // Add global touch listeners for edge swipe
  useEffect(() => {
    // Only add listeners on mobile
    const isMobile = window.innerWidth < 1024;
    if (!isMobile) return;
    
    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd, { passive: true });
    
    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd]);
  
  // Calculate sidebar position based on state and drag
  const getSidebarTransform = () => {
    if (isDragging) {
      if (isOpen) {
        // Open + dragging: translate based on negative offset
        return `translateX(${dragOffset}px)`;
      } else {
        // Closed + dragging: translate from -100% plus positive offset
        return `translateX(calc(-100% + ${dragOffset}px))`;
      }
    }
    // Not dragging: use CSS classes
    return undefined;
  };
  
  // Add Admin Master item if applicable
  const navItems = isAdminMaster 
    ? [...navigationItems, {
        id: 'admin',
        label: 'Admin Master',
        icon: 'Crown',
        type: 'group',
        children: [
          { label: 'Instâncias', path: '/admin/instances', icon: 'Crown' },
        ],
      }]
    : navigationItems;
  
  return (
    <>
      {/* Backdrop */}
      {(isOpen || isDragging) && (
        <div
          className={cn(
            'lg:hidden fixed inset-0 bg-black/60 backdrop-blur-sm z-50',
            !isDragging && 'animate-in fade-in duration-200'
          )}
          style={{
            opacity: isDragging 
              ? isOpen 
                ? Math.max(0, 1 + dragOffset / 320)
                : Math.min(1, dragOffset / 320)
              : 1
          }}
          onClick={onClose}
          data-testid="mobile-sidebar-backdrop"
        />
      )}
      
      {/* Sidebar Panel */}
      <aside
        ref={sidebarRef}
        className={cn(
          'lg:hidden fixed top-0 left-0 h-full w-[85%] max-w-[320px] bg-card z-50',
          'shadow-2xl',
          !isDragging && 'transition-transform duration-300 ease-out',
          !isDragging && (isOpen ? 'translate-x-0' : '-translate-x-full')
        )}
        style={{
          transform: isDragging ? getSidebarTransform() : undefined,
        }}
        data-testid="mobile-sidebar"
      >
        {/* Swipe hint indicator */}
        <div 
          className={cn(
            'absolute right-0 top-1/2 -translate-y-1/2 w-1 h-16 bg-primary/30 rounded-l-full',
            'transition-opacity duration-200',
            isDragging ? 'opacity-100' : 'opacity-0'
          )}
        />
        
        {/* Header */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-border">
          <Link 
            to="/" 
            className="flex items-center gap-3"
            onClick={onClose}
          >
            <div className="w-10 h-10 rounded-xl overflow-hidden shadow-sm">
              <img 
                src={logoSrc} 
                alt={businessName} 
                className="w-full h-full object-contain"
                data-testid="mobile-sidebar-logo"
              />
            </div>
            <div>
              <span className="font-bold text-lg">{businessName}</span>
              <p className="text-xs text-muted-foreground">Sistema de Gestão</p>
            </div>
          </Link>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="h-10 w-10 rounded-xl"
            data-testid="mobile-sidebar-close"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>
        
        {/* Instance Selector (Admin Master only) */}
        {isAdminMaster && (
          <div className="p-3 border-b border-border">
            <InstanceSelector />
          </div>
        )}
        
        {/* Navigation */}
        <ScrollArea className="h-[calc(100%-4rem)] px-3 py-4">
          <nav className="space-y-1">
            {navItems.map((item) => {
              if (item.type === 'link') {
                const isActive = location.pathname === item.path;
                return (
                  <MobileNavLink
                    key={item.id}
                    item={item}
                    isActive={isActive}
                    onClick={onClose}
                  />
                );
              }
              
              if (item.type === 'group') {
                return (
                  <MobileNavGroup
                    key={item.id}
                    item={item}
                    onItemClick={onClose}
                  />
                );
              }
              
              return null;
            })}
          </nav>
          
          {/* Swipe hint text for first-time users */}
          <div className="mt-6 px-4 py-3 bg-muted/50 rounded-lg">
            <p className="text-xs text-muted-foreground text-center">
              Deslize para a esquerda para fechar
            </p>
          </div>
          
          {/* Bottom spacing */}
          <div className="h-20" />
        </ScrollArea>
      </aside>
      
      {/* Edge swipe indicator (shows when closed, on left edge) */}
      {!isOpen && !isDragging && (
        <div 
          className="lg:hidden fixed left-0 top-1/2 -translate-y-1/2 w-1 h-20 bg-primary/20 rounded-r-full z-40"
          data-testid="swipe-edge-indicator"
        />
      )}
    </>
  );
}
